/**
 * The health metrics strip — player entries and prize delivery on the Contest
 * Overview (design-system.md §6 "Health KPI strip"; `.health` in the prototype's
 * contest-overview.css).
 *
 * The hairline between cells is a 1px grid gap over a `--line` background rather
 * than a border on either cell: two adjacent 0.5px borders would stack into an
 * uneven 1px, and the shared gap can't drift out of alignment. `overflow:
 * hidden` is what lets the white cells take the container's rounded corners.
 *
 * `idle` is the whole point of the component. §7's "no stats slop" rule says a
 * draft contest must not show zeroes dressed as measurements, so the strip — not
 * the caller — decides that an idle metric renders as an em-dash. The caller
 * passes the real figure either way and does not have to know the rule.
 */
import Box from '@mui/material/Box';
import type { Theme } from '@mui/material/styles';

export interface KpiCell {
  label: string;
  value: string;
  foot: string;
  /** 0–100. Only the metrics that are a share of something carry a bar. */
  progress?: number;
}

interface KpiStripProps {
  cells: KpiCell[];
  /**
   * The contest has not started producing these numbers yet — draft and
   * scheduled. Values become em-dashes and the footnotes go italic.
   */
  idle?: boolean;
}

export const KpiStrip = ({ cells, idle = false }: KpiStripProps) => (
  <Box
    sx={(theme: Theme) => ({
      display: 'grid',
      gridTemplateColumns: `repeat(${cells.length}, 1fr)`,
      gap: '1px',
      backgroundColor: theme.tokens.color.line,
      borderRadius: `${theme.tokens.radius.insetTile}px`,
      border: theme.tokens.border.default,
      overflow: 'hidden',
    })}
  >
    {cells.map((cell) => (
      <Box
        key={cell.label}
        sx={(theme: Theme) => ({
          backgroundColor: theme.tokens.color.surface,
          padding: '18px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
        })}
      >
        <Box
          component="span"
          sx={(theme: Theme) => ({
            fontSize: '12px',
            fontWeight: 500,
            color: theme.tokens.color.ink3,
          })}
        >
          {cell.label}
        </Box>

        <Box
          component="span"
          sx={(theme: Theme) => ({
            fontFamily: theme.tokens.font.mono,
            fontSize: theme.tokens.text.kpiValue.fontSize,
            fontWeight: theme.tokens.text.kpiValue.fontWeight,
            letterSpacing: theme.tokens.text.kpiValue.letterSpacing,
            lineHeight: 1,
            fontVariantNumeric: 'tabular-nums',
            color: idle ? theme.tokens.color.ink4 : theme.tokens.color.ink,
          })}
        >
          {idle ? '—' : cell.value}
        </Box>

        {/* Above the footnote, as the design stacks it — and the track is drawn
            while idle too, empty, so the cell does not reflow the moment the
            first award lands. The em-dash is what keeps an idle cell honest;
            hiding the track as well would only make it jump later. */}
        {cell.progress === undefined ? null : (
          <Box
            sx={(theme: Theme) => ({
              height: '4px',
              // 8 on top of the cell's 6px gap, per `.bar` in the prototype.
              marginTop: '8px',
              borderRadius: `${theme.tokens.radius.badgeSharp}px`,
              backgroundColor: theme.tokens.componentColor.barTrack,
              overflow: 'hidden',
            })}
          >
            <Box
              role="progressbar"
              aria-label={cell.label}
              aria-valuenow={idle ? 0 : cell.progress}
              aria-valuemin={0}
              aria-valuemax={100}
              sx={(theme: Theme) => ({
                display: 'block',
                height: '100%',
                width: `${idle ? 0 : cell.progress}%`,
                backgroundColor: theme.tokens.color.accent,
                borderRadius: `${theme.tokens.radius.badgeSharp}px`,
                transition: 'width 0.6s ease',
              })}
            />
          </Box>
        )}

        <Box
          component="span"
          sx={(theme: Theme) => ({
            fontSize: '12.5px',
            // 2 on top of the cell's 6px gap, per `.foot` in the prototype.
            marginTop: '2px',
            color: theme.tokens.color.ink4,
            fontStyle: idle ? 'italic' : 'normal',
          })}
        >
          {cell.foot}
        </Box>
      </Box>
    ))}
  </Box>
);
